<script setup lang="ts">
  const {
    items,
    activeTab,
    bottomLine = false,
    isFill = false,
  } = defineProps<{
    items: { title: string; value: string; width?: string; disabled?: boolean }[];
    activeTab: string;
    bottomLine?: boolean;
    isFill?: boolean;
  }>();
  const emit = defineEmits(["changeTab"]);

  const handleClickTab = (nextActiveTab: string) => emit("changeTab", nextActiveTab);
</script>

<template>
  <div class="tabs">
    <div
      v-for="tab in items"
      :key="tab.value"
      :class="['tab', { 'tab-fill': isFill }, { 'tab-active': tab.value === activeTab }, { 'tab-disabled': tab.disabled }]"
      :style="{ width: tab.width }"
      @click="tab.disabled ? undefined : handleClickTab(tab.value)"
      data-testid="tab"
    >
      <span>{{ tab.title }}</span>
      <span v-if="tab.value !== activeTab && !tab.disabled" class="tab-dash-hover" />
      <span v-if="tab.value === activeTab" class="tab-dash" />
    </div>
    <hr v-if="bottomLine" class="tabs-line" />
  </div>
  <slot />
</template>

<style scoped>
  @media (hover: hover) {
    .tab:hover {
      cursor: pointer;
    }

    .tab:hover .tab-dash {
      background-color: var(--sf-button-primary-background-hover);
    }

    .tab:hover .tab-dash-hover {
      visibility: visible;
    }
  }

  .tabs {
    display: flex;
    position: relative;
    overflow-x: auto;
  }

  .tab-fill {
    flex: 1;
  }

  .tabs-line {
    position: absolute;
    height: 1px;
    background: var(--sf-base-border-primary);
    width: 100%;
    bottom: 0;
    margin: 0;
    border: none;
  }

  .tab {
    font-size: 16px;
    color: var(--sf-base-text-secondary);
    position: relative;
    padding: 12px 8px 0 8px;
    gap: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tab-active {
    color: var(--sf-base-text-primary);
    font-weight: 500;
  }

  .tab-disabled {
    cursor: not-allowed;
    pointer-events: none;
  }

  .tab-dash {
    width: calc(100% - 4px);
    height: 4px;
    background-color: var(--sf-button-primary-background-default);
    border-radius: 100px 100px 0 0;
    z-index: 1;
  }

  .tab-dash-hover {
    width: calc(100% - 4px);
    height: 2px;
    background-color: var(--sf-base-text-tertiary);
    border-radius: 100px 100px 0 0;
    z-index: 1;
    visibility: hidden;
  }
</style>
